import React from 'react';
import { BarChart3, FileText, FolderOpen, Users, RefreshCw, AlertCircle } from 'lucide-react';
import { useDashboardData } from '../hooks/useDashboardData';
import { ComingSoonOverlay } from './ComingSoonOverlay';

interface StatCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  error?: string | null;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  description,
  icon,
  isLoading = false,
  error = null,
}) => {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="mt-2 h-9 w-20 bg-muted animate-pulse rounded" />
          ) : error ? (
            <div className="mt-2 flex items-center gap-2">
              <AlertCircle size={16} className="text-destructive" />
              <span className="text-sm text-destructive">---</span>
            </div>
          ) : (
            <h3 className="mt-2 text-3xl font-bold">{value}</h3>
          )}
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>
        <div className="rounded-full bg-primary/10 p-3 text-primary">
          {icon}
        </div>
      </div>
    </div>
  );
};

export const DashboardOverview: React.FC = () => {
  const { stats, isLoading, error, lastUpdated, refresh } = useDashboardData();

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Documents"
          value={formatNumber(stats.totalDocuments)}
          description={`Last updated ${lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}`}
          icon={<FileText size={24} />}
          isLoading={isLoading}
          error={error}
        />
        <StatCard
          title="Collections"
          value={formatNumber(stats.totalCollections)}
          description={`Last updated ${lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}`}
          icon={<FolderOpen size={24} />}
          isLoading={isLoading}
          error={error}
        />
        <StatCard
          title="Users"
          value={formatNumber(stats.totalUsers)}
          description={`Last updated ${lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}`}
          icon={<Users size={24} />}
          isLoading={isLoading}
          error={error}
        />
        <ComingSoonOverlay>
          <StatCard
            title="Analytics"
            value="12.5k"
            description="Views this month"
            icon={<BarChart3 size={24} />}
          />
        </ComingSoonOverlay>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-destructive" />
              <span className="text-sm text-destructive font-medium">
                Failed to load dashboard data
              </span>
            </div>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-2 text-sm text-destructive hover:underline"
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{error}</p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <ComingSoonOverlay>
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-medium">Recent Activity</h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <FileText size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium">New document uploaded</p>
                  <p className="text-xs text-muted-foreground">
                    John Doe uploaded "Board Meeting Minutes - April 2025"
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    2 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <Users size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium">New user registered</p>
                  <p className="text-xs text-muted-foreground">
                    Jane Smith joined as Editor
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    5 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <FolderOpen size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium">New collection created</p>
                  <p className="text-xs text-muted-foreground">
                    Admin created "Historical Photographs 1950-1960"
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    1 day ago
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ComingSoonOverlay>

        <ComingSoonOverlay>
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-medium">Documents Awaiting Review</h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <FileText size={16} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      OBTC Financial Report 1978
                    </p>
                    <button className="text-xs text-primary hover:underline">
                      Review
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Submitted by: John Doe
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Waiting for 2 days
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <FileText size={16} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      Historical Newspaper Clipping - 1965
                    </p>
                    <button className="text-xs text-primary hover:underline">
                      Review
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Submitted by: Jane Smith
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Waiting for 1 day
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-2 text-primary">
                  <FileText size={16} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      Board Meeting Minutes - March 1992
                    </p>
                    <button className="text-xs text-primary hover:underline">
                      Review
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Submitted by: Robert Johnson
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Waiting for 3 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ComingSoonOverlay>
      </div>
    </div>
  );
};

export default DashboardOverview;
